// NPM MODULES
import React, { useState, Fragment, useEffect, useCallback } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

// FORMATTED MESSAGES
import AddressBookFormattedMessages from '../../../messages/websms/AddressBookFormattedMessages';

// NETWORKING
import useAddressBookRequest from '../../../requests/AddressBookRequest';

// COMPONENTS
import Loader from '../../common/Loader';

const Manage = (props) => {
  const [list, setList] = useState('personal');
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [deleteResult, setDeleteResult] = useState(false);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState();
  const { getAddressbookCounts, deleteAllContacts } = useAddressBookRequest();

  const getData = useCallback(async () => {
    setLoading(true);
    const counts = await getAddressbookCounts(props.errorHandler);
    console.log('manage counts:', counts);
    setCounts(counts);
    setLoading(false);
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleModal = (selected) => {
    setList(selected);
    setDeleteModal(!showDeleteModal);
  };

  /*
    Handles when the Delete All button is clicked for either the Personal
    or Global Contacts list.
  */
  const handleDeleteAll = async () => {
    const result = await deleteAllContacts(list, props.errorHandler);

    setDeleteModal(false);
    setDeleteResult(result ? 'Delete Successful' : 'Delete Failed');
  };

  /*
    Displays a success message after deleting all Contacts from a given list type.
  */
  const displayDeleteResult = (listParam) => {
    if (deleteResult && listParam === list) {
      return <Alert sx={{ marginTop: 2 }}>{deleteResult}</Alert>;
    }
  };

  if (loading) return <Loader sx={{ marginTop: 2 }} />;

  return (
    <Fragment>
      <TableContainer sx={{ marginTop: 8, maxWidth: 800 }}>
        <Table
          sx={{
            '& .MuiTableRow-root': {
              backgroundColor: 'common.white',
            },
            '& .MuiTableCell-root': {
              borderRight: '1px solid',
              borderColor: 'primary.main',
            },
            '& .MuiTableCell-root:last-child': {
              borderRight: 'none',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell align="center">
                {AddressBookFormattedMessages.globalListText}
              </TableCell>
              <TableCell align="center">
                {AddressBookFormattedMessages.personalListText}
              </TableCell>
              <TableCell align="center">
                {AddressBookFormattedMessages.globalBlacklistText}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{ verticalAlign: 'top', borderBottom: 'none' }}
              >
                <Typography color="primary" fontWeight={700} fontSize={13}>
                  {AddressBookFormattedMessages.totalContactText}
                </Typography>
              </TableCell>
              <TableCell align="center">
                {counts
                  ? counts.globalCount
                  : AddressBookFormattedMessages.noData}
              </TableCell>
              <TableCell align="center">
                {counts
                  ? counts.personalCount
                  : AddressBookFormattedMessages.noData}
              </TableCell>
              <TableCell align="center">
                {counts
                  ? counts.blacklistCount
                  : AddressBookFormattedMessages.noData}
              </TableCell>
            </TableRow>
            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  borderBottom: 'none',
                },
              }}
            >
              <TableCell align="center">
                {/** If user is customer user, make sure there's no "Delete Global Contact" button */}
                {(props.accessLevel > '0' || props.accessType !== '0') && (
                  <Button
                    onClick={() => handleModal('global')}
                    variant="contained"
                    color="secondary"
                  >
                    {AddressBookFormattedMessages.deleteText}
                  </Button>
                )}
              </TableCell>
              <TableCell align="center">
                <Button
                  onClick={() => handleModal('personal')}
                  variant="contained"
                  color="secondary"
                >
                  {AddressBookFormattedMessages.deleteText}
                </Button>
              </TableCell>
              <TableCell align="center"></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {displayDeleteResult('global')}
      {displayDeleteResult('personal')}

      <Dialog open={showDeleteModal} onClose={handleModal}>
        <DialogTitle variant="h2">
          {list === 'personal'
            ? AddressBookFormattedMessages.deletePersonalContactsHeader
            : AddressBookFormattedMessages.deleteGlobalContactsHeader}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {list === 'personal'
              ? AddressBookFormattedMessages.deletePersonalContactsBody
              : AddressBookFormattedMessages.deleteGlobalContactsBody}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModal} variant="link" color="secondary">
            {AddressBookFormattedMessages.cancelText}
          </Button>
          <Button
            onClick={handleDeleteAll}
            variant="contained"
            color="secondary"
          >
            {AddressBookFormattedMessages.deleteContactText}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default Manage;
